import { useQueryClient } from '@tanstack/react-query';
import { getCustomerNotes } from '@/lib/demo-data/customer-notes';
import {
  addCustomerNoteDemoResponse,
  updateCustomerNoteDemoResponse,
} from '@/services/demo/customer-notes-demo.service';
import type {
  CreateNoteRequest,
  CustomerNote,
  CustomerNoteApiItem,
  CustomerNotesApiResponse,
  DeleteNoteRequest,
  UpdateNoteRequest,
} from '@/types/customer-note';
import { useApi } from './useApi';
import { useModeAwareMutation, useModeAwareQuery } from './useModeAwareQuery';
import { useBulkDeleteNotes } from './useBulkDeleteNotes';

function mapApiItemToCustomerNote(item: CustomerNoteApiItem): CustomerNote {
  return {
    id: String(item.id),
    customerId: String(item.customerId),
    content: item.content,
    createdAt: item.createdAt,
    createdBy: {
      id: item.createdByUserId === null ? '' : String(item.createdByUserId),
      name: item.createdByUserName,
    },
  };
}

/**
 * Hook to fetch customer notes
 * Handles both Demo and Live modes
 */
export function useCustomerNotes(customerId: string | undefined) {
  const { apiGet } = useApi();
  const mockNotes = customerId ? getCustomerNotes(customerId) : [];

  return useModeAwareQuery<CustomerNote[], Error>(
    ['customer-notes', customerId],
    async ({ signal }) => {
      if (!customerId) return [];
      const response = await apiGet<CustomerNotesApiResponse>(
        `/api/v1/customers/${customerId}/notes`,
        undefined,
        signal,
      );
      return (response.notes || []).map(mapApiItemToCustomerNote);
    },
    mockNotes,
    {
      enabled: !!customerId,
      staleTime: 0,
      refetchOnMount: 'always',
    },
  );
}

/**
 * Hook to add a new customer note
 * Handles both Demo and Live modes
 */
export function useAddCustomerNote() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<CustomerNote, Error, CreateNoteRequest & { userId: string; userName: string }>(
    async (request) => {
      const response = await apiPost<CustomerNoteApiItem>(`/api/v1/customers/${request.customerId}/notes`, {
        content: request.content,
        transactionId: request.transactionId,
      });
      return mapApiItemToCustomerNote(response);
    },
    addCustomerNoteDemoResponse,
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({ queryKey: ['customer-notes', variables.customerId] });
        queryClient.invalidateQueries({ queryKey: ['customer-audit-logs'] });
      },
    },
  );
}

/**
 * Hook to edit an existing customer note
 * Handles both Demo and Live modes
 */
export function useUpdateCustomerNote() {
  const { apiPut } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<CustomerNote, Error, UpdateNoteRequest>(
    async (request) => {
      const response = await apiPut<CustomerNoteApiItem>(
        `/api/v1/customers/${request.customerId}/notes/${request.noteId}`,
        { content: request.content },
      );
      return mapApiItemToCustomerNote(response);
    },
    updateCustomerNoteDemoResponse,
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({ queryKey: ['customer-notes', variables.customerId] });
        queryClient.invalidateQueries({ queryKey: ['customer-audit-logs'] });
      },
    },
  );
}

/**
 * Hook to delete a customer note
 * Handles both Demo and Live modes
 */
export function useDeleteCustomerNote() {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<void, Error, DeleteNoteRequest>(
    async (request) => {
      await apiDelete<void>(`/api/v1/customers/${request.customerId}/notes/${request.noteId}`);
    },
    () => undefined,
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({ queryKey: ['customer-notes', variables.customerId] });
        queryClient.invalidateQueries({ queryKey: ['customer-audit-logs'] });
      },
    },
  );
}

/**
 * Hook to delete several customer notes in one call
 * Handles both Demo and Live modes
 */
export function useDeleteBulkCustomerNotes() {
  return useBulkDeleteNotes('/api/v1/customers/notes/bulk-delete', ['customer-notes', 'customer-audit-logs']);
}
